// 📄 pages/Proyectos/components/ArrangementsSection.tsx
// Sub-fase K7: sección "Communication Arrangements" embebida en la vista de
// cada solución (SolucionPlaceholder.tsx para SLP/Sourcing/Contracts,
// CommerceHome.tsx para Commerce) — solo se monta en proyectos Cloud, el
// caller es responsable de ese gate (ver esErpCloud).
import { useEffect, useState } from 'react';
import { Workflow, Plus, RefreshCw, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  getArrangements, crearArrangement, actualizarArrangement, eliminarArrangement,
  Arrangement, SolucionConArrangements
} from '../../../services/arrangementService.ts';
import { getComunicacionSystems, ComunicacionSystem } from '../../../services/comunicacionService.ts';
import ArrangementModal from './ArrangementModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import Alert from '../../../components/ui/Alert.tsx';

interface Props {
  projectId: string;
  solucion: SolucionConArrangements;
}

export default function ArrangementsSection({ projectId, solucion }: Props) {
  const { user, profile } = useAuth();
  const esAdmin = profile?.role === 'admin';

  const [arrangements, setArrangements] = useState<Arrangement[]>([]);
  const [sistemas, setSistemas] = useState<ComunicacionSystem[]>([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Arrangement | null>(null);
  const [aEliminar, setAEliminar] = useState<Arrangement | null>(null);

  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'warning'>('success');
  const showAlert = (msg: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const [arrs, sys] = await Promise.all([getArrangements(projectId, solucion), getComunicacionSystems(projectId)]);
      setArrangements(arrs);
      setSistemas(sys);
    } catch (err) {
      console.error('Error cargando Communication Arrangements:', err);
      showAlert('No se pudieron cargar los Communication Arrangements.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, solucion]);

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500 flex items-center justify-center gap-2 text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" /> Cargando Communication Arrangements...
      </div>
    );
  }

  return (
    <div>
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage('')} />

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-medium text-cyan-200 flex items-center gap-2">
          <Workflow className="w-5 h-5" /> Communication Arrangements
        </h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nuevo Arrangement
        </button>
      </div>

      {arrangements.length === 0 ? (
        <div className="p-8 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Workflow className="w-7 h-7 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Todavía no hay Communication Arrangements configurados para esta solución.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {arrangements.map((a) => {
            const sistema = sistemas.find((s) => s.id === a.comunicacionSystemId);
            return (
              <div key={a.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{a.nombre}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border text-cyan-300 bg-cyan-500/10 border-cyan-500/30">
                      {a.scenarioId}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    → {sistema?.nombre || 'Communication System no encontrado'}
                    {a.arrangementId && ` · ${a.arrangementId}`}
                  </p>
                  {a.descripcion && <p className="text-xs text-gray-500 mt-1">{a.descripcion}</p>}
                </div>
                <button onClick={() => setEditando(a)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0" title="Editar">
                  <Pencil className="w-4 h-4" />
                </button>
                {esAdmin && (
                  <button onClick={() => setAEliminar(a)} className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(modalOpen || editando) && (
        <ArrangementModal
          arrangement={editando}
          sistemas={sistemas}
          onSave={async (data) => {
            if (editando) await actualizarArrangement(projectId, solucion, editando.id, data);
            else await crearArrangement(projectId, solucion, data, user?.uid || '');
            await cargar();
            showAlert(editando ? 'Arrangement actualizado.' : 'Arrangement creado.');
          }}
          onClose={() => { setModalOpen(false); setEditando(null); }}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este Communication Arrangement?"
          message={`"${aEliminar.nombre}" se eliminará. Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={async () => {
            try {
              await eliminarArrangement(projectId, solucion, aEliminar.id);
              setAEliminar(null);
              await cargar();
              showAlert('Arrangement eliminado.');
            } catch (err: any) {
              showAlert(err.message || 'Error al eliminar.', 'error');
            }
          }}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
